import { useState } from 'react';
import { CountryFlag } from '../ui/CountryFlag';
import { ShareBar } from '../ui/charts/ShareBar';
import { useOfferLookup, useCampaignLookup } from '../../hooks/useLookups';

type TabId = 'country' | 'offer' | 'device' | 'os' | 'source' | 'campaign';

interface TabData {
  label: React.ReactNode;
  clicks: number;
  quality: number;
  bots: number;
  convs?: number;
  revenue?: number;
}

interface DimensionTabsProps {
  byCountry: Array<{ clicksTotal: number; clicksQuality: number; clicksBot: number; conversions?: number; revenue?: number; country: string | null }>;
  byOffer: Array<{ clicksTotal: number; clicksQuality: number; clicksBot: number; conversions?: number; revenue?: number; offerId: number | null }>;
  byDevice: Array<{ device: string | null; clicksTotal: number; clicksQuality: number; clicksBot: number }>;
  byOs: Array<{ os: string | null; clicksTotal: number; clicksQuality: number; clicksBot: number }>;
  bySource: Array<{ source: string | null; clicksTotal: number; clicksQuality: number; clicksBot: number }>;
  byCampaign: Array<{ campaignId: number | null; conversions: number; revenue: number }>;
}

const TABS: { id: TabId; title: string; icon: React.ReactNode }[] = [
  { id: 'country', title: 'Country', icon: <Globe size={14} /> },
  { id: 'offer', title: 'Offer', icon: <Tag size={14} /> },
  { id: 'device', title: 'Device', icon: <Smartphone size={14} /> },
  { id: 'os', title: 'OS', icon: <Monitor size={14} /> },
  { id: 'source', title: 'Source', icon: <Wifi size={14} /> },
  { id: 'campaign', title: 'Campaign', icon: <Target size={14} /> },
];

export function DimensionTabs({
  byCountry,
  byOffer,
  byDevice,
  byOs,
  bySource,
  byCampaign,
}: DimensionTabsProps): JSX.Element {
  const [tab, setTab] = useState<TabId>('country');
  const offerLookup = useOfferLookup();
  const campaignLookup = useCampaignLookup();

  const tabMap: Record<TabId, TabData[]> = {
    country: byCountry.map((r) => ({
      label: r.country ? (
        <CountryFlag iso2={r.country} showCode size={16} />
      ) : (
        <span className="text-text-tertiary italic">unknown</span>
      ),
      clicks: r.clicksTotal,
      quality: r.clicksQuality,
      bots: r.clicksBot,
      convs: r.conversions ?? 0,
      revenue: r.revenue ?? 0,
    })),
    offer: byOffer.map((r) => ({
      label: r.offerId == null ? (
        <span className="text-text-tertiary italic">unlinked</span>
      ) : (
        <span className="text-text-secondary">{offerLookup.getName(r.offerId)}</span>
      ),
      clicks: r.clicksTotal,
      quality: r.clicksQuality,
      bots: r.clicksBot,
      convs: r.conversions ?? 0,
      revenue: r.revenue ?? 0,
    })),
    device: byDevice.map((r) => ({
      label: r.device || <span className="text-text-tertiary italic">unknown</span>,
      clicks: r.clicksTotal,
      quality: r.clicksQuality,
      bots: r.clicksBot,
    })),
    os: byOs.map((r) => ({
      label: r.os || <span className="text-text-tertiary italic">unknown</span>,
      clicks: r.clicksTotal,
      quality: r.clicksQuality,
      bots: r.clicksBot,
    })),
    source: bySource.map((r) => ({
      label: r.source || <span className="text-text-tertiary italic">direct</span>,
      clicks: r.clicksTotal,
      quality: r.clicksQuality,
      bots: r.clicksBot,
    })),
    campaign: byCampaign.map((r) => ({
      label: r.campaignId == null ? (
        <span className="text-text-tertiary italic">unlinked</span>
      ) : (
        <span className="text-text-secondary">{campaignLookup.nameFromId(r.campaignId)}</span>
      ),
      clicks: 0,
      quality: 0,
      bots: 0,
      convs: r.conversions,
      revenue: r.revenue,
    })),
  };

  const hasRevenueMetric = ['country', 'offer', 'campaign'].includes(tab);
  const data = tabMap[tab];

  return (
    <div>
      {/* Tab navigation */}
      <div className="flex items-center gap-1 overflow-x-auto pb-2 border-b border-border-subtle">
        {TABS.map(({ id, title, icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`
              flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-display uppercase tracking-wider rounded-sm transition-colors whitespace-nowrap
              ${tab === id
                ? 'bg-neon-cyan/20 text-neon-cyan border border-neon-cyan/30'
                : 'text-text-tertiary hover:text-text-secondary hover:bg-bg-tertiary'}
            `}
          >
            {icon}
            {title}
          </button>
        ))}
      </div>

      {/* Tab content - table */}
      <div className="px-3 py-2 bg-bg-tertiary/50 border border-border-subtle rounded mt-2">
        {data.length === 0 ? (
          <p className="text-[11px] font-mono text-text-tertiary italic py-1">no data</p>
        ) : (
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-text-tertiary text-[10px] uppercase border-b border-border-subtle">
                <th className="text-left py-1.5">Label</th>
                <th className="text-right">Clicks</th>
                <th className="text-right">Quality</th>
                <th className="text-right">Bot%</th>
                {hasRevenueMetric && <th className="text-right">Conv</th>}
                {hasRevenueMetric && <th className="text-right">CR</th>}
                {hasRevenueMetric && <th className="text-right">Revenue</th>}
                <th className="text-right w-[80px]">Share</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => {
                const total = row.bots + row.quality + (row.clicks - row.bots - row.quality);
                const botRate = total > 0 ? (row.bots / total) * 100 : 0;
                const convs = row.convs ?? 0;
                const cr = row.quality > 0 ? (convs / row.quality) * 100 : 0;
                const revenue = row.revenue ?? 0;
                
                // Bot% color coding
                let botColor = 'text-text-tertiary';
                if (total > 0) {
                  if (botRate >= 20) botColor = 'text-neon-red';
                  else if (botRate >= 10) botColor = 'text-neon-yellow';
                }

                // ShareBar segments
                const shareSegments = data.map((d, idx) => {
                  const val = hasRevenueMetric ? (d.revenue ?? 0) : d.clicks;
                  return {
                    value: Math.max(0, val),
                    className: idx === i ? 'bg-neon-cyan' : 'bg-neon-cyan/40',
                  };
                });

                return (
                  <tr key={i} className="border-b border-border-subtle/40">
                    <td className="py-1.5">{row.label}</td>
                    <td className="text-right text-neon-cyan numeric">{formatNumber(row.clicks)}</td>
                    <td className="text-right text-neon-magenta numeric">{formatNumber(row.quality)}</td>
                    <td className={`text-right font-bold ${botColor}`}>{total > 0 ? `${botRate.toFixed(1)}%` : '—'}</td>
                    {hasRevenueMetric && <td className="text-right text-text-secondary numeric">{formatNumber(convs)}</td>}
                    {hasRevenueMetric && <td className="text-right text-neon-green numeric">{row.quality > 0 ? formatPercent(cr, 1) : '—'}</td>}
                    {hasRevenueMetric && <td className="text-right text-neon-yellow numeric">{formatCurrency(revenue)}</td>}
                    <td className="py-1">
                      <ShareBar
                        segments={shareSegments}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Import formatting utilities inline to avoid circular deps
function formatNumber(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatPercent(n: number, decimals: number = 1): string {
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n) + '%';
}

// Icon imports
import { Globe, Tag, Smartphone, Monitor, Wifi, Target } from 'lucide-react';
